import React from 'react';

import createMentionPlugin, {
  defaultSuggestionsFilter,
} from '../DraftJSPlugins/draft-js-mention-plugin/src';
import { Mentionable } from '../types';

export const useMentionPlugin = ({
  isUrlInput,
  mentions,
  lastValue,
  position,
  mentionTrigger = '{',
  isKeyPressing,
}: {
  isUrlInput?: boolean;
  isKeyPressing: boolean;
  mentionTrigger?: string;
  position?: string;
  lastValue?: string | null;
  mentions?: Mentionable[];
}) => {
  const [mentionSuggestionsOpen, setMentionSuggestionsOpen] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState(mentions);

  const mentionSuggestionsPlugin = React.useMemo(() => {
    return mentions
      ? createMentionPlugin({
          mentionTrigger,
          entityMutability: 'IMMUTABLE',
          isUrlInput: Boolean(isUrlInput),
        })
      : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(mentions)]);

  const { MentionSuggestions } = mentionSuggestionsPlugin ?? { MentionSuggestions: null };
  const onOpenChange = React.useCallback((open: boolean) => {
    setMentionSuggestionsOpen(open);
  }, []);

  const onSearchChange = React.useCallback(
    ({ value, selectedValue }: { value: string; selectedValue: string }): void => {
      setSuggestions(
        defaultSuggestionsFilter(value, selectedValue, mentions, isUrlInput, lastValue)
      );
    },
    [isUrlInput, lastValue, mentions]
  );

  const mentionSuggestions = React.useMemo(() => {
    if (!MentionSuggestions) return null;
    return (
      <MentionSuggestions
        position={position}
        isKeyPressing={isKeyPressing}
        whitespaceAfterMention={false}
        onlyAddText={true}
        open={mentionSuggestionsOpen}
        onOpenChange={onOpenChange}
        suggestions={suggestions}
        onSearchChange={onSearchChange}
      />
    );
  }, [
    MentionSuggestions,
    mentionSuggestionsOpen,
    onOpenChange,
    onSearchChange,
    isKeyPressing,
    suggestions,
    position,
  ]);

  React.useEffect(() => {
    setSuggestions(mentions);
  }, [mentions]);

  return {
    mentionSuggestions,
    mentionSuggestionsOpen,
    mentionSuggestionsPlugin,
  };
};
